import { fieldNameConverter } from '../../License/helpers/fieldNameConverter';
import { Flow } from '../../License/types/enums';

import { type ILicenseTypeResponse } from '../../License/types/response';
import { HeaderTabFields } from '../../License/types/fieldEnums';
import { LicenseTypeNature } from '../../../types';
import { type IComparisonField } from '../types';

import { formatDate } from '../../../helpers/dateHelpers';
import formatNumber from '../../../helpers/formatNumber';

function getHeaderTabFields({
  licenseType,
  fieldConfigs,
  licenseAbbreviation,
  locale = 'en',
  t,
}: {
  licenseType: ILicenseTypeResponse;
  fieldConfigs: Record<string, { hidden: boolean }>;
  licenseAbbreviation: string;
  locale: string;
  t: (key: string, options?: Record<string, string>) => string;
}): IComparisonField[][] {
  const {
    featureFlags: { invoiceValueManagementEnabled },
    flow,
    licenseTypeNature,
  } = licenseType;

  const headerTabFields = [
    [
      {
        label: t('Type of {{licenseAbbreviation}}', { licenseAbbreviation: t(licenseAbbreviation) }),
        fieldKey: HeaderTabFields.licenseType,
      },
      {
        label: 'User Reference Number',
        fieldKey: HeaderTabFields.userReferenceNumber,
      },
    ],
    [
      {
        label: 'Request Number',
        fieldKey: HeaderTabFields.requestNumber,
      },
      {
        label: 'Request Date',
        fieldKey: HeaderTabFields.requestDate,
        render: (value: string) => formatDate(value),
      },
      {
        label: 'Approval Reference',
        fieldKey: HeaderTabFields.approvalReference,
      },
      {
        label: 'Approval Date',
        fieldKey: HeaderTabFields.approvalDate,
        render: (value: string) => formatDate(value),
      },
      {
        label: 'Valid From',
        fieldKey: HeaderTabFields.validFrom,
        render: (value: string) => formatDate(value),
      },
      {
        label: 'Valid To',
        fieldKey: HeaderTabFields.validTo,
        render: (value: string) => formatDate(value),
      },
    ],
  ];
  const countryOfExportSectionFields = [];

  if (
    fieldConfigs[fieldNameConverter(HeaderTabFields.countryOfDestinationCode)]?.hidden !== true &&
    licenseTypeNature !== LicenseTypeNature.EPHYTO
  ) {
    countryOfExportSectionFields.push({
      label: flow === Flow.EX ? 'Country of Destination' : 'Country of Export',
      fieldKey: HeaderTabFields.countryOfDestinationCode,
      tagKey: HeaderTabFields.countryOfDestinationName,
    });
  }

  if (fieldConfigs[fieldNameConverter(HeaderTabFields.customsOfficeCode)]?.hidden !== true) {
    countryOfExportSectionFields.push({
      label: flow === Flow.IM ? 'Entry Point' : 'Exit Point',
      fieldKey: HeaderTabFields.customsOfficeCode,
      tagKey: HeaderTabFields.customsOfficeName,
    });
  }

  if (fieldConfigs[fieldNameConverter(HeaderTabFields.departmentOfficeCode)]?.hidden !== true) {
    countryOfExportSectionFields.push({
      label: 'Department Office',
      fieldKey: HeaderTabFields.departmentOfficeCode,
      tagKey: HeaderTabFields.departmentOfficeName,
    });
  }

  if (fieldConfigs[fieldNameConverter(HeaderTabFields.typeOfUse)]?.hidden !== true) {
    countryOfExportSectionFields.push({
      label: 'Type of Use',
      fieldKey: HeaderTabFields.typeOfUse,
      render: (value: string) => t(value),
    });
  }

  if (countryOfExportSectionFields.length) {
    headerTabFields.push(countryOfExportSectionFields);
  }

  const termsOfDeliveryAndInvoiceFields = [];

  if (fieldConfigs[fieldNameConverter(HeaderTabFields.termsOfDeliveryCode)]?.hidden !== true) {
    termsOfDeliveryAndInvoiceFields.push({
      label: 'Terms of Delivery',
      fieldKey: HeaderTabFields.termsOfDeliveryCode,
      tagKey: HeaderTabFields.termsOfDeliveryDescription,
    });
  }

  if (invoiceValueManagementEnabled) {
    termsOfDeliveryAndInvoiceFields.push(
      {
        label: 'Invoice Currency Code',
        fieldKey: HeaderTabFields.invoiceCurrencyCode,
        tagKey: HeaderTabFields.invoiceExchangeRate,
        renderTag: (value: string) => formatNumber({ value, locale }) as string,
      },
      {
        label: 'Invoice Value in Foreign Currency',
        fieldKey: HeaderTabFields.invoiceAmountInForeignCurrency,
        render: (value: string) => formatNumber({ value, locale }) as string,
      },
      {
        label: 'Invoice Value in National Currency',
        fieldKey: HeaderTabFields.invoiceAmountInNationalCurrency,
        render: (value: string) => formatNumber({ value, locale }) as string,
      }
    );
  }

  if (termsOfDeliveryAndInvoiceFields.length) {
    headerTabFields.push(termsOfDeliveryAndInvoiceFields);
  }

  const placeOfLoadingAndUnloadingFields = [];

  if (fieldConfigs[fieldNameConverter(HeaderTabFields.placeOfLoadingCode)]?.hidden !== true) {
    placeOfLoadingAndUnloadingFields.push({
      label: 'Place of Loading',
      fieldKey: HeaderTabFields.placeOfLoadingCode,
      tagKey: HeaderTabFields.placeOfLoadingName,
    });
  }

  if (fieldConfigs[fieldNameConverter(HeaderTabFields.placeOfUnloadingCode)]?.hidden !== true) {
    placeOfLoadingAndUnloadingFields.push({
      label: 'Place of Unloading',
      fieldKey: HeaderTabFields.placeOfUnloadingCode,
      tagKey: HeaderTabFields.placeOfUnloadingName,
    });
  }

  if (placeOfLoadingAndUnloadingFields.length) {
    headerTabFields.push(placeOfLoadingAndUnloadingFields);
  }

  return headerTabFields;
}

export default getHeaderTabFields;
